import { Button } from "@/components/ui/UIElements/Button";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Switch } from "../";
import { Pagination } from "../Data";
import { useGlobalUtils } from "@finitless/shared/lib";

const ProductsList = ({
  products,
  productsPagination,
  isLoadingProducts,
  isError = false,
  onRetry,
  productPage,
  onPageChange,
  selectedProduct,
  onSelectProduct,
  productSearchInput,
  onSearchChange,
  onClearSearch,
  hasPosIntegration,
  selectedCategoryId,
  onCreateProduct,
  onToggleProduct,
  isLoadingToggle,
  showCategoryContext = false,
  searchPlaceholder = "Search products...",
  categoryLabelPrefix = "",
  uncategorizedLabel = "",
}) => {
  const { t } = useTranslation(["stores", "common"]);
  const { parsePrice } = useGlobalUtils();
  const searchInputRef = useRef(null);

  const clearSearchAndRestoreFocus = () => {
    onClearSearch();
    searchInputRef.current?.focus();
  };

  const handleProductClick = (product) => {
    // Siempre abrir modal de extras (que tiene tabs de Extras y Edit Product)
    onSelectProduct(product);
  };

  const getCategoryName = (product) => {
    const categoryName =
      product?.category_name ??
      product?.categoryName ??
      product?.category?.name ??
      product?.category?.title;

    if (categoryName) {
      return categoryName;
    }

    return uncategorizedLabel;
  };

  return (
    <div
      className="ordering-catalog-surface border-border bg-background flex h-full flex-col rounded-lg border"
      data-ordering-expression="product"
      style={{
        backgroundColor: "var(--od-surface)",
        borderColor: "var(--od-border)",
      }}
    >
      {/* Header */}
      <div className="border-border border-b p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Package" className="text-muted-foreground h-5 w-5" />
            <h3 className="text-lg font-semibold text-white">Products</h3>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            {/* Product Search */}
            <div className="relative w-full sm:w-80">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="text-muted-foreground h-4 w-4" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                aria-label={searchPlaceholder}
                placeholder={searchPlaceholder}
                value={productSearchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                className="border-border bg-card focus:ring-ring w-full rounded-lg border py-2 pr-10 pl-10 text-sm text-white placeholder-gray-400 focus:ring-2 focus:outline-none"
              />
              {productSearchInput && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={clearSearchAndRestoreFocus}
                  aria-label={t("search.clearSearch")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <Icon
                    name="X"
                    className="text-muted-foreground h-4 w-4 hover:text-white"
                  />
                </Button>
              )}
            </div>

            {!hasPosIntegration && selectedCategoryId && (
              <div className="sm:ml-auto">
                <Button
                  variant="default"
                  type="button"
                  className="flex items-center gap-1 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={onCreateProduct}
                  disabled={!selectedCategoryId}
                >
                  <Icon name="Plus" className="h-4 w-4" /> Add Product
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isError ? (
          <div role="alert" className="p-6 text-center">
            <p className="text-destructive">{t("common:states.error")}</p>
            <Button type="button" className="mt-4" onClick={() => onRetry()}>
              {t("common:actions.retry")}
            </Button>
          </div>
        ) : isLoadingProducts ? (
          <div className="flex h-full items-center justify-center">
            <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="space-y-2">
              {products.map((product) => (
                <div
                  key={product.id}
                  className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors ${
                    selectedProduct?.id === product.id
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card hover:border-border"
                  }`}
                  onClick={() => handleProductClick(product)}
                >
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-medium text-white">
                      <Button
                        variant="ghost"
                        type="button"
                        className="max-w-full truncate rounded text-left"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleProductClick(product);
                        }}
                      >
                        {product.name}
                      </Button>
                    </h4>
                    {product.description && (
                      <p className="text-muted-foreground mt-1 line-clamp-1 truncate text-xs">
                        {product.description}
                      </p>
                    )}
                    {showCategoryContext && (
                      <p className="text-primary mt-1 text-xs">
                        {categoryLabelPrefix}: {getCategoryName(product)}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 flex items-center gap-3">
                    <span className="text-success text-sm font-semibold">
                      {parsePrice(product.price || 0)}
                    </span>
                    <Switch
                      aria-label={product.name}
                      checked={!!product.enabled}
                      onCheckedChange={(value) => {
                        if (onToggleProduct) {
                          onToggleProduct(product.id, value);
                        }
                      }}
                      disabled={isLoadingToggle}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Product Pagination */}
            {productsPagination.total_pages > 1 && (
              <div className="border-border mt-4 border-t pt-4">
                <Pagination
                  currentPage={productPage}
                  totalPages={productsPagination.total_pages}
                  onPageChange={onPageChange}
                  maxVisiblePages={5}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <Icon
                name="Package"
                className="text-muted-foreground mx-auto mb-2 h-12 w-12"
              />
              <p className="text-muted-foreground text-sm">No products found</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsList;
